import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Timer, Play } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShareBar } from "@/components/ShareBar";
import { publicApi, videoUrl } from "@/lib/api";
import { useVisitor } from "@/lib/visitor";
import { useStreak, StreakNote } from "@/components/DevotionalStreak";

const seen = () => JSON.parse(localStorage.getItem("vp_dev_seen") || "[]");

export const DevotionalPopup = () => {
  const { profile, promptOpen } = useVisitor();
  const [dev, setDev] = useState(null);
  const [open, setOpen] = useState(false);
  const [watching, setWatching] = useState(false);
  const [played, setPlayed] = useState(false);
  const { streak, isMember, onWatch } = useStreak();

  useEffect(() => {
    if (promptOpen || !localStorage.getItem("vp_prompted")) return;
    const today = format(new Date(), "yyyy-MM-dd");
    let t;
    publicApi.get("/devotionals").then((r) => {
      const d = r.data.find((x) => x.devotional_date === today);
      if (!d || seen().includes(d.id) || sessionStorage.getItem("vp_dev_later") === d.id) return;
      setDev(d);
      t = setTimeout(() => setOpen(true), 1800);
    }).catch(() => {});
    return () => clearTimeout(t);
  }, [promptOpen]);

  const later = () => { sessionStorage.setItem("vp_dev_later", dev.id); setOpen(false); };
  const watch = () => { localStorage.setItem("vp_dev_seen", JSON.stringify([...seen(), dev.id].slice(-60))); setWatching(true); };
  const close = (o) => { if (!o) { if (!watching) later(); else setOpen(false); } };
  if (!dev) return null;
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[94vh] overflow-y-auto rounded-3xl bg-white p-0 sm:max-w-md" data-testid="devotional-popup">
        {!watching ? (
          <div className="p-7">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#EBF3FA] text-[#1D64B4]"><Timer className="h-6 w-6" /></span>
            <DialogTitle className="mt-5 text-2xl font-bold tracking-tight text-slate-900" data-testid="devotional-popup-greeting">Hi{profile?.nickname ? ` ${profile.nickname}` : ""}, warm greetings from your Victory Parañaque Team!</DialogTitle>
            <DialogDescription className="mt-3 text-base text-slate-600">There's a new 2-minute devotional uploaded for today. Do you want to view it now or later?</DialogDescription>
            <div className="mt-5 rounded-2xl bg-slate-50 p-4">
              <p className="font-semibold text-slate-900" data-testid="devotional-popup-title">{dev.title}</p>
              <p className="text-sm text-slate-500">{dev.speaker}{dev.scripture && ` · ${dev.scripture}`}</p>
            </div>
            <div className="mt-6 grid gap-2 sm:grid-cols-2">
              <Button onClick={watch} className="rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid="devotional-popup-now"><Play className="mr-1 h-4 w-4" /> View it now</Button>
              <Button variant="outline" onClick={later} className="rounded-full" data-testid="devotional-popup-later">Maybe later</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 p-5">
            <DialogTitle className="sr-only">{dev.title}</DialogTitle>
            <video src={videoUrl(dev.id)} controls autoPlay playsInline className="mx-auto aspect-[9/16] max-h-[62vh] rounded-2xl bg-black object-cover" onPlay={() => { if (!played) { setPlayed(true); onWatch(); } }} data-testid="devotional-popup-video" />
            {played && isMember && <StreakNote streak={streak} testId="devotional-popup-streak" />}
            <div><p className="font-semibold text-slate-900">{dev.title}</p><p className="text-sm text-slate-500">{dev.speaker}{dev.scripture && ` · ${dev.scripture}`}</p></div>
            <ShareBar title={dev.title} url={`${process.env.REACT_APP_BACKEND_URL}/api/share/devotionals/${dev.id}`} testPrefix="devotional-popup-share" />
            <Link to="/devotionals" className="block text-sm font-semibold text-[#1D64B4]" data-testid="devotional-popup-all">See all devotionals</Link>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
